import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, CheckCircle2, Star, Clock } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, Spinner, AuthedAvatar, BlueCheck } from "../components/common";
import PinModal from "../components/PinModal";

function Avatar({ src, name, size = 48 }) {
  return <AuthedAvatar src={src} name={name} size={size} className="rounded-full" />;
}

export default function Send({ initialMode = "send" }) {
  const nav = useNavigate();
  const [mode] = useState(initialMode); // "send" | "request" — driven by route
  const [step, setStep] = useState(1); // 1 input, 2 confirm, 3 done
  const [tag, setTag] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [saved, setSaved] = useState(false);
  const [pinRequired, setPinRequired] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sugOpen, setSugOpen] = useState(false);

  useEffect(() => { api.get("/contacts").then(({ data }) => setContacts(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/security/pin").then(({ data }) => setPinRequired(data.pin_set && data.pin_enabled)).catch(() => {}); }, []);

  useEffect(() => {
    const q = tag.trim();
    if (!sugOpen || q.replace(/^\$/, "").length < 1) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/users/suggest", { params: { q } }); setSuggestions(data.results); }
      catch { setSuggestions([]); }
    }, 180);
    return () => clearTimeout(t);
  }, [tag, sugOpen]);

  const pickSuggestion = (c) => { setTag(c.cash_tag); setSugOpen(false); setSuggestions([]); };

  const saveContact = async () => {
    try { await api.post("/contacts", { cash_tag: quote.recipient.cash_tag }); setSaved(true); toast.success("Added to contacts"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const getQuote = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!tag.trim()) { toast.error("Enter a Cash Tag"); return; }
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/transfers/quote", { cash_tag: tag, amount: amt });
      setQuote(data); setStep(2);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const doTransfer = async () => {
    setShowPin(false);
    setBusy(true);
    const key = `idem_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    try {
      const { data } = await api.post("/transfers",
        { cash_tag: tag, amount: parseFloat(amount), note: note || null },
        { headers: { "Idempotency-Key": key } });
      setResult(data); setStep(3);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const confirm = () => {
    if (pinRequired) { setShowPin(true); return; }
    doTransfer();
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!tag.trim()) { toast.error("Enter a Cash Tag"); return; }
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post("/requests", { cash_tag: tag, amount: amt, note: note || null });
      toast.success("Request sent");
      nav("/app/requests", { state: { tab: "outgoing" } });
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  // Send success screen
  if (mode === "send" && step === 3 && result) {
    if (result.status === "pending_approval") {
      return (
        <div className="animate-fade-up text-center pt-10" data-testid="send-pending-approval">
          <div className="mx-auto grid place-items-center w-20 h-20 rounded-full bg-warning/10 text-warning"><Clock size={44} /></div>
          <h1 className="font-display text-3xl font-light tracking-tight mt-6">Waiting for approval</h1>
          <div className="amount-figure text-4xl mt-2">{result.amount_display}</div>
          <p className="text-muted-foreground mt-2">to {quote.recipient.display_name} · {quote.recipient.cash_tag}</p>
          <p className="text-sm text-muted-foreground mt-4 max-w-xs mx-auto">Your parent needs to approve this payment. You'll be notified once they do.</p>
          <div className="space-y-3 mt-10">
            <button data-testid="send-done-btn" onClick={() => nav("/app")} className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold">Done</button>
          </div>
        </div>
      );
    }
    return (
      <div className="animate-fade-up text-center pt-10">
        <div className="mx-auto grid place-items-center w-20 h-20 rounded-full bg-success/10 text-success"><CheckCircle2 size={44} /></div>
        <h1 className="font-display text-3xl font-light tracking-tight mt-6">Sent!</h1>
        <div className="amount-figure text-4xl mt-2">{result.amount_display}</div>
        <p className="text-muted-foreground mt-2">to {quote.recipient.display_name} · {quote.recipient.cash_tag}</p>
        <div className="space-y-3 mt-10">
          {!saved && (
            <button data-testid="send-save-contact" onClick={saveContact} className="w-full h-12 rounded-2xl bg-secondary font-semibold flex items-center justify-center gap-2 text-sm">
              <Star size={16} /> Save {quote.recipient.display_name} to contacts
            </button>
          )}
          <button data-testid="send-done-btn" onClick={() => nav("/app")} className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold">Done</button>
          <button data-testid="send-view-activity" onClick={() => nav("/app/activity")} className="w-full h-14 rounded-2xl bg-secondary font-semibold">View in Activity</button>
        </div>
      </div>
    );
  }

  const contactStrip = contacts.length > 0 && (
    <div>
      <label className="text-xs font-bold tracking-wide uppercase text-muted-foreground">Contacts</label>
      <div className="flex gap-3 overflow-x-auto no-scrollbar mt-2 pb-1">
        {contacts.map((c) => (
          <button key={c.contact_id} type="button" data-testid={`contact-${c.contact_id}`} onClick={() => setTag(c.cash_tag)}
            className="flex flex-col items-center gap-1.5 shrink-0 w-16">
            <AuthedAvatar src={c.profile_photo} name={c.display_name} size={48} className="rounded-full" />
            <span className="text-[11px] truncate w-full text-center">{c.display_name}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const isReq = mode === "request";

  return (
    <div className="animate-fade-up">
      <PageHeader title={isReq ? "Request Money" : "Send Money"}
        subtitle={step === 1 ? (isReq ? "Ask someone to pay you" : "Enter recipient and amount") : "Confirm the details"} />

      {step === 1 && (
        <>
          <form onSubmit={isReq ? submitRequest : getQuote} className="space-y-4">
            {contactStrip}
            <div>
              <label className="text-xs font-bold tracking-wide uppercase text-muted-foreground">{isReq ? "Request from (Cash Tag)" : "Send to (Cash Tag)"}</label>
              <div className="relative mt-2">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
                <input data-testid="send-cashtag-input" value={tag} autoComplete="off"
                  onChange={(e) => { setTag(e.target.value); setSugOpen(true); }}
                  onFocus={() => setSugOpen(true)}
                  onBlur={() => setTimeout(() => setSugOpen(false), 150)}
                  placeholder="$jackyboytrze"
                  className="w-full h-14 pl-12 pr-4 rounded-2xl bg-secondary border border-transparent focus:border-primary focus:bg-card outline-none font-mono" />
                {sugOpen && suggestions.length > 0 && (
                  <div data-testid="cashtag-suggestions"
                    className="absolute z-30 left-0 right-0 top-full mt-2 rounded-2xl bg-card border border-border shadow-xl overflow-hidden max-h-72 overflow-y-auto">
                    {suggestions.map((c) => (
                      <button type="button" key={c.cash_tag} data-testid={`suggestion-${c.cash_tag}`}
                        onMouseDown={(e) => { e.preventDefault(); pickSuggestion(c); }}
                        className="w-full flex items-center gap-3 p-3 text-left hover:bg-secondary/60 transition-colors border-b border-border last:border-0">
                        <AuthedAvatar src={c.profile_photo} name={c.display_name} size={40} className="rounded-full shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-sm truncate">{c.display_name}</span>
                            {c.blue_check && <BlueCheck size={15} testid={`sug-badge-${c.cash_tag}`} />}
                          </div>
                          <div className="text-xs text-muted-foreground font-mono truncate">{c.cash_tag}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold tracking-wide uppercase text-muted-foreground">Amount</label>
              <div className="relative mt-2">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-light text-muted-foreground">$</span>
                <input data-testid="send-amount-input" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
                  className="w-full h-16 pl-10 pr-4 rounded-2xl bg-secondary border border-transparent focus:border-primary focus:bg-card outline-none amount-figure text-3xl" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold tracking-wide uppercase text-muted-foreground">Note (optional)</label>
              <input data-testid="send-note-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={140} placeholder="What's it for?"
                className="w-full h-14 px-4 mt-2 rounded-2xl bg-secondary border border-transparent focus:border-primary focus:bg-card outline-none" />
            </div>
            <button type="submit" disabled={busy} data-testid={isReq ? "request-continue-btn" : "send-continue-btn"}
              className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center shadow-[0_12px_32px_rgba(75,35,204,0.3)] transition-transform duration-200 active:scale-[0.98] disabled:opacity-60">
              {busy ? <Spinner className="w-5 h-5" /> : (isReq ? "Send request" : "Continue")}
            </button>
          </form>
        </>
      )}

      {mode === "send" && step === 2 && quote && (
        <div className="space-y-5">
          <div className="rounded-3xl bg-card border border-border p-6 text-center">
            <Avatar src={quote.recipient.profile_photo} name={quote.recipient.display_name} size={64} />
            <div className="mt-3 flex items-center justify-center gap-1.5">
              <span className="font-display text-xl font-semibold">{quote.recipient.display_name}</span>
              {quote.recipient.blue_check && <BlueCheck size={16} />}
            </div>
            <div className="text-sm text-muted-foreground font-mono">{quote.recipient.cash_tag}</div>
          </div>

          <div className="rounded-3xl bg-secondary/60 p-6 space-y-3">
            {[["Amount", quote.amount_display], ["Fee", quote.fee_display]].map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span>
              </div>
            ))}
            <div className="h-px bg-border" />
            <div className="flex justify-between">
              <span className="font-semibold">Total</span>
              <span data-testid="confirm-total" className="font-semibold amount-figure text-xl">{quote.total_display}</span>
            </div>
            {note && <p className="text-xs text-muted-foreground pt-1">Note: {note}</p>}
          </div>

          <div className="space-y-3">
            <button data-testid="send-confirm-btn" onClick={confirm} disabled={busy}
              className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center shadow-[0_12px_32px_rgba(75,35,204,0.3)] transition-transform duration-200 active:scale-[0.98] disabled:opacity-60">
              {busy ? <Spinner className="w-5 h-5" /> : `Confirm & Send ${quote.amount_display}`}
            </button>
            <button data-testid="send-back-btn" onClick={() => setStep(1)} className="w-full h-14 rounded-2xl bg-secondary font-semibold">Back</button>
          </div>
        </div>
      )}

      <PinModal open={showPin} onClose={() => setShowPin(false)} onSuccess={doTransfer}
        title="Confirm with your PIN" subtitle={`Sending ${quote?.amount_display || ""}`} />
    </div>
  );
}
